import React from 'react';
import { AppHeader } from '../components/AppHeader';
import type { AuthUser } from '../hooks/useAuth';
import { FaceBadge } from '../components/FaceBadge';
import { hasFeature } from '../utils/featureFlags';
import MyCertificates from '../components/MyCertificates';
import { MyAvatarEditor } from '../components/MyAvatarEditor';

interface Props { user: AuthUser; }

const Divider: React.FC = () => <div style={{ height: 1, background: '#f1f5f9', marginLeft: 16 }} />;

export const ProfilePage: React.FC<Props> = ({ user }) => {
  const roleLabel = user.user_role === 'captain' ? '船長' : '船員';

  const InfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
    <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 12 }}>
      <div style={{ fontSize: 13, color: '#94a3b8', width: 80, flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', fontFamily: mono ? 'monospace' : 'inherit', flex: 1 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>

      {/* ヘッダー */}
      <AppHeader title="プロフィール" />

      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 32 }}>

        {/* アバターカード */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 20px 24px', background: 'white', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ marginBottom: 14 }}>
            <MyAvatarEditor user={user} size={80}
              fallbackStyle={{ background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: 'white', fontSize: 34, boxShadow: '0 4px 16px rgba(14,165,233,0.3)' }} />
          </div>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#0f172a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            {user.user_name}
            <FaceBadge userId={user.user_id} size="md" />
          </div>
          <span style={{ background: '#eff6ff', color: '#0ea5e9', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>{roleLabel}</span>
        </div>

        {/* 基本情報 */}
        <div style={{ padding: '0 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', padding: '0 4px', marginBottom: 6, marginTop: 24 }}>基本情報</div>
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <InfoRow label='氏名' value={user.user_name} />
            <Divider />
            <InfoRow label='ロール' value={roleLabel} />
            <Divider />
            <InfoRow label='社員コード' value={user.short_id ?? '-'} mono />
            <Divider />
            <InfoRow label='企業ID' value={user.company_id} mono />
          </div>
        </div>

        {/* 資格・証明書の有効期限（機能フラグ crew_certificates の会社）。船長には自船の乗組員分も */}
        {hasFeature('crew_certificates') && <MyCertificates isOfficer={user.user_role === 'captain'} />}

        {/* セキュリティ */}
        <div style={{ padding: '0 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', padding: '0 4px', marginBottom: 6, marginTop: 24 }}>セキュリティ</div>
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div
              onClick={() => {}}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', cursor: 'pointer' }}
              onTouchStart={e => (e.currentTarget.style.background = '#f8fafc')}
              onTouchEnd={e => (e.currentTarget.style.background = 'white')}
            >
              <div style={{ width: 38, height: 38, borderRadius: 11, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🔐</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>顔認証を登録</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>WebAuthnでログインをより安全に</div>
              </div>
              <span style={{ color: '#cbd5e1', fontSize: 20 }}>›</span>
            </div>
            <Divider />
            <div
              onClick={() => {}}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', cursor: 'pointer' }}
              onTouchStart={e => (e.currentTarget.style.background = '#f8fafc')}
              onTouchEnd={e => (e.currentTarget.style.background = 'white')}
            >
              <div style={{ width: 38, height: 38, borderRadius: 11, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🔑</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>パスワードを変更</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>ログインパスワードを更新する</div>
              </div>
              <span style={{ color: '#cbd5e1', fontSize: 20 }}>›</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
